import React, { useContext } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import dynamic from 'next/dynamic';
import Col from '~/grid/Col';
import { CopyStandard } from '../../atoms/Copytext';
import { getAddressFromPayload } from '~/helpers/getFormattedAddress';
import Row from '~/grid/Row';
import { mq } from '~/styles/grid';
import { Headline500Skin } from '../../atoms/Headline/HeadlineSkins';
import { CopyStandardSkin } from '../../atoms/Copytext/CopytextSkins';
import Icon from '../../atoms/Icon';
import { getColorByName } from '~/helpers/getColors';
import { LanguageContext } from '~/helpers/LanguageContext';
import { useIntl } from 'react-intl';

/**
 * @type {React.ComponentType<IcalPropsType>}
 */
const ICalLinkNoSSR = dynamic(() => import('./partials/iCalLink'), {
  ssr: false,
});

const EventContainer = styled.div`
  width: 100%;
  padding: 25px;
  margin-bottom: 3px;
  background-color: ${(props) => props.theme.color.gray100};
  @media (${mq.lgDown}) {
    padding: 25px;
    margin-bottom: 5px;
  }

  &&& a {
    color: ${(props) => props.theme.color.primary800};
    &:hover,
    &:hover svg {
      color: ${(props) => props.theme.color.cta} !important;
    }
  }
`;

const ColStyled = styled(Col)`
  margin-bottom: 0;
`;

const EventDate = styled.span`
  ${CopyStandardSkin};
  display: block;
  margin: 0;
  color: ${(props) =>
    getColorByName(
      props.theme,
      props.theme.copytext.copyStandard.textColor,
    )} !important;
`;

const StyledTextHeadline = styled.span`
  ${Headline500Skin};
  color: ${(props) => props.theme.color.primary800} !important;
  display: block;
  padding-bottom: 10px;
  text-decoration: none;
`;

const StyledLinkHeadline = styled.a`
  ${Headline500Skin};
  color: ${(props) => props.theme.color.primary800} !important;
  display: block;
  padding-bottom: 10px;
  text-decoration: none;
`;

const StyledCopy = styled(CopyStandard)`
  color: ${(props) =>
    getColorByName(
      props.theme,
      props.theme.copytext.copyStandard.textColor,
    )} !important;
`;

const LinkStyled = styled.div`
  margin: 13px 0 6px;
  color: ${(props) => props.theme.color.primary800} !important;
  a {
    border-bottom: none !important;
  }
`;

const StyledA = styled.a`
  color: ${(props) =>
    getColorByName(
      props.theme,
      props.theme.copytext.copyStandard.textColor,
    )} !important;
`;

const StyledIcon = styled(Icon)`
  color: ${(props) => props.theme.color.primary800} !important;
`;

/**
 * @param {EventTeaserProps} props
 * @returns {React.ReactElement} The rendered component
 */
const EventTeaser = ({ title, startTime, endTime, website, eventContact }) => {
  const lang = useContext(LanguageContext);
  const addressArr = getAddressFromPayload(eventContact);
  const intl = useIntl();

  const eventDate = (isoString, format) =>
    moment(isoString).locale(lang).format(format);

  const url = website?.url ?? '';

  const getEventData = () => {
    const joinAddressLine = (addressLine) => addressLine.join(' ');
    const getAdressString = addressArr
      .map((addressLine) => joinAddressLine(addressLine))
      .join(', ');
    const event = {
      startTime,
      endTime,
      description: '',
      title,
      url,
      location: getAdressString,
    };
    return event;
  };

  return (
    <EventContainer>
      <Row>
        <ColStyled colAmount={{ xl: 4, lg: 4, md: 12, sm: 12, xs: 12 }}>
          <EventDate>{eventDate(startTime, 'll')}</EventDate>
          <EventDate>{eventDate(startTime, 'LT')}</EventDate>
        </ColStyled>
        <Col colAmount={{ xl: 8, lg: 8, md: 12, sm: 12, xs: 12 }}>
          {url.length > 0 && (
            <StyledLinkHeadline href={url}>{title}</StyledLinkHeadline>
          )}
          {url.length === 0 && <StyledTextHeadline>{title}</StyledTextHeadline>}
          <address>
            {addressArr.map((addressLine) => (
              <StyledCopy key={addressLine.join()}>
                {addressLine.join(' ')}
              </StyledCopy>
            ))}
          </address>
          {website && (
            <LinkStyled>
              <StyledA href={website.url} target={website.target || '_blank'}>
                {website.text || website.url}
              </StyledA>
            </LinkStyled>
          )}

          <ICalLinkNoSSR eventData={getEventData()}>
            <span className="iconBefore">
              <StyledIcon icon="CALENDAR" />
            </span>
            {intl.formatMessage({
              id: 'event.addToCalendar',
            })}
          </ICalLinkNoSSR>
        </Col>
      </Row>
    </EventContainer>
  );
};

export default EventTeaser;
